import type { CtaConfig, FieldConfig, FieldKey, ResultsConfig, SeverityId } from './types';

/**
 * Pure calculation + formatting helpers for the SettlementCalculator.
 *
 * The estimate formula is intentionally hard-coded; only `scalingFactor` is
 * editor-configurable (see `register.ts`). The multiplier represents the full
 * total (not an additive non-economic ratio) and is applied to the injury base
 * (medical + future), with lost wages added back at face value:
 *
 *     low  = ((medical + future) × MULTIPLIER_LOW  + wages) × scalingFactor
 *     high = ((medical + future) × MULTIPLIER_HIGH + wages) × scalingFactor
 */
export const MULTIPLIER_LOW = 4;
export const MULTIPLIER_HIGH = 7;

export const DEFAULT_SCALING_FACTOR = 0.4;

/** Field defaults live here (not in register.ts) so component code can import them
 * without triggering Builder.io's registration side-effects. */
export const DEFAULT_FIELDS: Record<FieldKey, FieldConfig> = {
	medical: {
		label: 'Medical bills & surgery costs',
		max: 500000,
		step: 2500,
		defaultValue: 15000,
	},
	wages: {
		label: 'Lost wages & earning capacity',
		max: 500000,
		step: 2500,
		defaultValue: 10000,
	},
	future: {
		label: 'Future medical & ongoing care',
		max: 750000,
		step: 2500,
		defaultValue: 5000,
	},
};

export const FIELD_ORDER: FieldKey[] = ['medical', 'wages', 'future'];

export const DEFAULT_RESULTS: ResultsConfig = {
	label: 'Your estimated range',
	lowTag: 'Early resolution',
	lowDescription: 'Insurer opening offer',
	highTag: 'Full litigation',
	highDescription: 'Attorney demand value',
	severityLabel: 'Severity',
};

export const DEFAULT_CTA: CtaConfig = {
	headline: 'Find Out What Your Case Could Be Worth',
	meta: 'No risk · No obligation · 100% free',
	buttonText: 'Start Your Free Case Review Now',
	buttonHref: '#form-container',
	subtext: 'Confidential · Takes less than 2 minutes',
};

export const SEV_ORDER: SeverityId[] = [
	'minor',
	'moderate',
	'significant',
	'severe',
	'catastrophic',
];

export const SEV_LABELS: Record<SeverityId, string> = {
	minor: 'Minor',
	moderate: 'Moderate',
	significant: 'Significant',
	severe: 'Severe',
	catastrophic: 'Catastrophic',
};

/** Injury-severity thresholds, keyed off the injury base (medical + future). */
const SEV_TIERS: { max: number; id: SeverityId }[] = [
	{ max: 1, id: 'minor' },
	{ max: 10000, id: 'minor' },
	{ max: 30000, id: 'moderate' },
	{ max: 80000, id: 'significant' },
	{ max: 250000, id: 'severe' },
	{ max: Infinity, id: 'catastrophic' },
];

export function clamp(val: number, min: number, max: number): number {
	return Math.min(max, Math.max(min, val));
}

/** Formats a value as whole dollars with thousands separators. Stored values are
 * already clamped to [0, max] upstream, so the displayed amount always matches state.
 *
 * With `compact`, values ≥ $1M are abbreviated to one-decimal millions ($1.5M, $2M).
 * Used only for the result cards, where multi-million estimates would otherwise
 * overflow the fixed-width card; inputs stay exact so a single step is always visible. */
export function fmt(val: number, compact = false): string {
	if (val <= 0) return '$0';
	if (compact && val >= 1_000_000) {
		return `$${(val / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
	}
	return `$${Math.round(val).toLocaleString('en-US')}`;
}

/** Parses a free-text money input, ignoring $, commas, and whitespace. */
export function parseInput(str: string): number {
	const s = str.replace(/[$,\s]/g, '');
	return Math.round(parseFloat(s)) || 0;
}

export function computeRange(
	values: Record<FieldKey, number>,
	scalingFactor: number,
): { low: number; high: number } {
	const injuryBase = values.medical + values.future;
	const low = (injuryBase * MULTIPLIER_LOW + values.wages) * scalingFactor;
	const high = (injuryBase * MULTIPLIER_HIGH + values.wages) * scalingFactor;
	return { low, high };
}

export function getSeverity(injuryBase: number): { id: SeverityId; index: number } {
	const tier = SEV_TIERS.find((t) => injuryBase < t.max) ?? SEV_TIERS[SEV_TIERS.length - 1];
	return { id: tier.id, index: SEV_ORDER.indexOf(tier.id) };
}
